import {
  Box,
  Button,
  Collapse,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import LogoBtn from "components/LogoBtn";
import EditIcon from "images/icons/EditIcon";
import SearchIcon from "images/icons/SearchIcon";
import toastErrorAndReport from "lib/toastErrorAndReport";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import SwitchIcon from "../../images/icons/SwitchIcon";
import * as ga from "../../lib/analytics";
import { useWindowDimensions } from "../../lib/windows";
import appStore from "../../store";
import AddressInputContent from "../Order/AddressInputContent";
import TimeSelectorContent from "../Order/TimeSelectorContent";
import { useSession } from "hooks/useSession";
import { getCityRegion } from "lib/delivery";
import { sendAppierEvent } from "lib/appier";

const search = (t, onClose) => {
  const {
    tempSelectedCity,
    tempSelectedZone,
    tempSelectedStation,
    tempSelectedZoneId,
    tempSelectedReturnCity,
    tempSelectedReturnZone,
    tempSelectedReturnStation,
    tempSelectedReturnZoneId,
    tempSelectedDays,
    tempSelectedStartClock,
    tempSelectedEndClock,
    isAirport,
    isAirportReturn,
    isSameAddress,
  } = appStore;

  if (isAirport === 0) {
    if (tempSelectedCity === "") {
      toastErrorAndReport(
        `${t("choose")}${t("city")}`,
        "HomeTimeSearchBar/search/pickup"
      );
      return;
    }
    if (tempSelectedZone === "") {
      toastErrorAndReport(
        `${t("choose")}${t("zone")}/${t("address")}`,
        "HomeTimeSearchBar/search/pickup"
      );
      return;
    }
    if (!tempSelectedZoneId) {
      toastErrorAndReport(
        `${t("choose")}${t("zone")}/${t("address")}`,
        "HomeTimeSearchBar/search/pickup"
      );
      return;
    }
  }

  if (isSameAddress === false) {
    if (isAirportReturn === 0) {
      if (tempSelectedReturnCity === "") {
        toastErrorAndReport(
          `${t("choose")}${t("city")}`,
          "HomeTimeSearchBar/search/return"
        );
        return;
      }
      if (tempSelectedReturnZone === "") {
        toastErrorAndReport(
          `${t("choose")}${t("zone")}/${t("address")}`,
          "HomeTimeSearchBar/search/return"
        );
        return;
      }
      if (!tempSelectedReturnZoneId) {
        toastErrorAndReport(
          `${t("choose")}${t("zone")}/${t("address")}`,
          "HomeTimeSearchBar/search/return"
        );
        return;
      }
    }
  }

  if (tempSelectedDays.length !== 2) {
    toastErrorAndReport(
      `${t("choose")}${t("date")}`,
      "HomeTimeSearchBar/search/date"
    );
    return;
  }

  if (
    tempSelectedDays[0].format("YYYY/MM/DD") ===
    tempSelectedDays[1].format("YYYY/MM/DD")
  ) {
    toastErrorAndReport(`${t("timeError")}`, "HomeTimeSearchBar/search/date");
    return;
  }

  if (tempSelectedStartClock === "" || tempSelectedEndClock === "") {
    toastErrorAndReport(
      `${t("choose")}${t("pickupTime")} & ${t("returnTime")}`,
      "HomeTimeSearchBar/search/time"
    );
    return;
  }

  if (isAirport === 0) {
    appStore.selectedCity = tempSelectedCity;
    appStore.selectedZone = tempSelectedZone;
    appStore.selectedStation = tempSelectedStation;
    appStore.selectedZoneId = tempSelectedZoneId;
  } else if (isAirport === 1) {
    appStore.selectedCity = "桃園市";
    appStore.selectedZone = "大園區";
    appStore.selectedStation = "機場第一航廈";
    appStore.selectedZoneId = 2;
  } else if (isAirport === 2) {
    appStore.selectedCity = "桃園市";
    appStore.selectedZone = "大園區";
    appStore.selectedStation = "機場第二航廈";
    appStore.selectedZoneId = 3;
  }
  if (isAirportReturn === 0) {
    appStore.selectedReturnCity = tempSelectedReturnCity;
    appStore.selectedReturnZone = tempSelectedReturnZone;
    appStore.selectedReturnStation = tempSelectedReturnStation;
    appStore.selectedReturnZoneId = tempSelectedReturnZoneId;
  } else if (isAirportReturn === 1) {
    appStore.selectedReturnCity = "桃園市";
    appStore.selectedReturnZone = "大園區";
    appStore.selectedReturnStation = "機場第一航廈";
    appStore.selectedReturnZoneId = 2;
  } else if (isAirportReturn === 2) {
    appStore.selectedReturnCity = "桃園市";
    appStore.selectedReturnZone = "大園區";
    appStore.selectedReturnStation = "機場第二航廈";
    appStore.selectedReturnZoneId = 3;
  }

  appStore.selectedDays = tempSelectedDays;
  appStore.selectedStartClock = tempSelectedStartClock;
  appStore.selectedEndClock = tempSelectedEndClock;

  onClose();

  fbq("track", "Search");
  ga.event({
    action: "search",
  });
  const searched_type =
    window?.location?.pathname === "/station" ? "大平台" : "外送到府";
  const searched_city = appStore.selectedCity;
  const searched_zone = appStore.selectedZone;
  const searched_station = appStore.selectedStation;
  const searched_city_region = getCityRegion(searched_city);
  sendAppierEvent("searched", {
    searched_type,
    searched_location: `${searched_city}${searched_zone}`,
    searched_city,
    searched_zone,
    searched_city_region,
    searched_station,
    service_name: "租車",
  });
  return;
};

const MobileBar = ({ display, isFilterOpen, onFilterClose, onFilterOpen }) => {
  const { t } = useTranslation("common");
  const {
    selectedCity,
    selectedZone,
    selectedStation,
    selectedReturnCity,
    selectedReturnZone,
    selectedReturnStation,
    selectedDays,
    selectedStartClock,
    selectedEndClock,
    isSameAddress,
  } = appStore;

  const router = useRouter();
  const { locale } = router;

  const {
    isOpen: isAddressTimeDrawerOpen,
    onOpen: onAddressTimeDrawerOpen,
    onClose: onAddressTimeDrawerClose,
  } = useDisclosure();

  const {
    isOpen: isTimeSelectorDrawerOpen,
    onOpen: onTimeSelectorDrawerOpne,
    onClose: onTimeSelectorDrawerClose,
  } = useDisclosure();

  const {
    isOpen: isAddressCollapseOpen,
    onOpen: onAddressCollapseOpen,
    onClose: onAddressCollapseClose,
  } = useDisclosure();

  const {
    isOpen: isReturnAddressCollapseOpen,
    onOpen: onReturnAddressCollapseOpen,
    onClose: onReturnAddressCollapseClose,
  } = useDisclosure();

  const {
    isOpen: isTimeSelectorCollapseOpen,
    onOpen: onTimeSelectorCollapseOpen,
    onClose: onTimeSelectorCollapseClose,
  } = useDisclosure();

  const closeAndSearch = () => {
    const onClose = () => {
      onTimeSelectorDrawerClose();
      onAddressTimeDrawerClose();
    };
    search(t, onClose);
  };

  return (
    <Flex
      pl="16px"
      pr="8px"
      py="8px"
      my="8px"
      display={display}
      bg="#F3F4F9"
      borderRadius="8px"
      alignItems="center"
      justify="space-between"
      border="1px solid #3374F6"
    >
      <Drawer
        autoFocus={false}
        size="full"
        isOpen={isAddressTimeDrawerOpen}
        placement="right"
        onClose={onAddressTimeDrawerClose}
      >
        <DrawerOverlay />
        <DrawerContent maxW="400px">
          <DrawerCloseButton size="lg" />
          <DrawerHeader>
            <LogoBtn isDisabled={true} />
            <Divider mt="16px" />
          </DrawerHeader>
          <DrawerBody>
            {isAddressCollapseOpen === false && (
              <Box
                p="16px"
                border="1px solid rgba(0, 0, 0, 0.1)"
                shadow="0px 2px 4px 2px rgba(0, 0, 0, 0.08)"
                borderRadius="8px"
                cursor="pointer"
                onClick={() => {
                  if (isTimeSelectorCollapseOpen) {
                    onTimeSelectorCollapseClose();
                  }
                  if (isReturnAddressCollapseOpen) {
                    onReturnAddressCollapseClose();
                  }
                  onAddressCollapseOpen();
                }}
              >
                <Text
                  fontSize="16px"
                  fontWeight="500"
                  lineHeight="23px"
                  color="rgba(170, 170, 170, 1)"
                  mb="8px"
                >
                  {t("pickupAddress")}
                </Text>
                {selectedCity !== "" ? (
                  <Text
                    textAlign="start"
                    fontSize="16px"
                    fontWeight="500"
                    lineHeight="23px"
                    color="rgba(51, 51, 51, 1)"
                  >
                    {t(`city_i18n.${selectedCity}`, {
                      defaultValue: selectedCity,
                    })}
                    {selectedZone !== "" &&
                      selectedZone !== null &&
                      `,${t(`zone_i18n.${selectedZone}`, {
                        defaultValue: selectedZone,
                      })}`}
                    {selectedStation !== "" &&
                      selectedStation !== null &&
                      selectedStation !== "chargeZone" &&
                      `,${t(`station_i18n.${selectedStation}`, {
                        defaultValue: selectedStation,
                      })}`}
                  </Text>
                ) : (
                  <Text
                    textAlign="start"
                    fontSize="16px"
                    fontWeight="500"
                    lineHeight="23px"
                    color="rgba(51, 51, 51, 1)"
                  >
                    {t("choose")}
                    {t("address")}
                  </Text>
                )}
                {isSameAddress && (
                  <Text
                    mt="8px"
                    fontSize="16px"
                    fontWeight="500"
                    lineHeight="24px"
                    color="#3374F6"
                  >
                    {t("dropoff_sameLocation")}
                  </Text>
                )}
              </Box>
            )}
            <Collapse in={isAddressCollapseOpen} animateOpacity>
              <Box
                border="1px solid rgba(0, 0, 0, 0.1)"
                shadow="0px 8px 16px 8px rgba(0, 0, 0, 0.08)"
                borderRadius="8px"
                p="16px"
              >
                <Text
                  fontSize="24px"
                  fontWeight="700"
                  color="#333333"
                  lineHeight="35px"
                  mb="16px"
                >
                  {t("wherePickup")}
                </Text>
                <AddressInputContent type="pickup" />
              </Box>
            </Collapse>
            {isReturnAddressCollapseOpen === false && (
              <Box
                my="24px"
                p="16px"
                border="1px solid rgba(0, 0, 0, 0.1)"
                shadow="0px 2px 4px 2px rgba(0, 0, 0, 0.08)"
                borderRadius="8px"
                cursor="pointer"
                onClick={() => {
                  if (isTimeSelectorCollapseOpen) {
                    onTimeSelectorCollapseClose();
                  }
                  if (isAddressCollapseOpen) {
                    onAddressCollapseClose();
                  }
                  onReturnAddressCollapseOpen();
                }}
              >
                <Text
                  fontSize="16px"
                  fontWeight="500"
                  lineHeight="23px"
                  color="rgba(170, 170, 170, 1)"
                  mb="8px"
                >
                  {t("returnAddress")}
                </Text>
                {isSameAddress ? (
                  <Text fontSize="16px" fontWeight="500" lineHeight="24px">
                    {t("dropoff_sameLocation")}
                  </Text>
                ) : selectedReturnCity !== "" ? (
                  <Text
                    textAlign="start"
                    fontSize="16px"
                    fontWeight="500"
                    lineHeight="23px"
                    color="rgba(51, 51, 51, 1)"
                  >
                    {t(`city_i18n.${selectedReturnCity}`, {
                      defaultValue: selectedReturnCity,
                    })}
                    {selectedReturnZone !== "" &&
                      selectedReturnZone !== null &&
                      `,${t(`zone_i18n.${selectedReturnZone}`, {
                        defaultValue: selectedReturnZone,
                      })}`}
                    {selectedReturnStation !== "" &&
                      selectedReturnStation !== null &&
                      selectedReturnStation !== "chargeZone" &&
                      `,${t(`station_i18n.${selectedReturnStation}`, {
                        defaultValue: selectedReturnStation,
                      })}`}
                  </Text>
                ) : (
                  <Text
                    textAlign="start"
                    fontSize="16px"
                    fontWeight="500"
                    lineHeight="23px"
                    color="rgba(51, 51, 51, 1)"
                  >
                    {t("choose")}
                    {t("address")}
                  </Text>
                )}
              </Box>
            )}
            <Collapse in={isReturnAddressCollapseOpen} animateOpacity>
              <Box
                mt="24px"
                border="1px solid rgba(0, 0, 0, 0.1)"
                shadow="0px 8px 16px 8px rgba(0, 0, 0, 0.08)"
                borderRadius="8px"
                p="16px"
              >
                <Text
                  fontSize="24px"
                  fontWeight="700"
                  color="#333333"
                  lineHeight="35px"
                  mb="16px"
                >
                  {t("whereDropoff")}
                </Text>
                <AddressInputContent type="return" />
              </Box>
            </Collapse>
            {isTimeSelectorCollapseOpen === false && (
              <Box
                mt="24px"
                p="16px"
                border="1px solid rgba(0, 0, 0, 0.1)"
                shadow="0px 2px 4px 2px rgba(0, 0, 0, 0.08)"
                borderRadius="8px"
                cursor="pointer"
                onClick={() => {
                  if (isAddressCollapseOpen) {
                    onAddressCollapseClose();
                  }
                  if (isReturnAddressCollapseOpen) {
                    onReturnAddressCollapseClose();
                  }
                  onTimeSelectorCollapseOpen();
                }}
              >
                <Text
                  fontSize="16px"
                  fontWeight="500"
                  lineHeight="23px"
                  color="rgba(170, 170, 170, 1)"
                  mb="8px"
                >
                  {t("useCarTimeTitle")}
                </Text>
                {selectedDays.length === 2 ? (
                  <Flex align="center">
                    <Text
                      fontSize="12px"
                      fontWeight="500"
                      lineHeight="17px"
                      color="rgba(51, 51, 51, 1)"
                    >
                      {selectedDays[0]?.format("MM/DD")}, {selectedStartClock}
                    </Text>
                    <SwitchIcon size="24px" color="rgba(51, 51, 51, 1)" />
                    <Text
                      fontSize="12px"
                      fontWeight="500"
                      lineHeight="17px"
                      color="rgba(51, 51, 51, 1)"
                    >
                      {selectedDays[1]?.format("MM/DD")}, {selectedEndClock}
                    </Text>
                  </Flex>
                ) : (
                  <Text
                    fontSize="16px"
                    fontWeight="500"
                    lineHeight="17px"
                    color="rgba(51, 51, 51, 1)"
                  >
                    {t("useCarTime")}
                  </Text>
                )}
              </Box>
            )}
            <Collapse in={isTimeSelectorCollapseOpen} animateOpacity>
              <Box
                mt="24px"
                border="1px solid rgba(0, 0, 0, 0.1)"
                shadow="0px 8px 16px 8px rgba(0, 0, 0, 0.08)"
                borderRadius="8px"
                p="16px"
              >
                <Text
                  fontSize="24px"
                  fontWeight="700"
                  color="#333333"
                  lineHeight="35px"
                  mb="16px"
                >
                  {t("wantUseCarTime")}
                </Text>
                <Box cursor="pointer" onClick={onTimeSelectorDrawerOpne}>
                  <Text fontSize="16px" fontWeight="500" lineHeight="23px">
                    {t("from")}
                  </Text>
                  <Flex
                    mt="8px"
                    mb="16px"
                    px="16px"
                    py="8px"
                    border="1px solid rgba(0, 0, 0, 0.1)"
                    borderRadius="8px"
                  >
                    {selectedDays[0] ? (
                      <Text
                        color="rgba(51, 51, 51, 1)"
                        fontSize="16px"
                        fontWeight="700"
                        lineHeight="23px"
                      >
                        {selectedDays[0]?.format("MM/DD")}, {selectedStartClock}
                      </Text>
                    ) : (
                      <Text
                        color="rgba(170, 170, 170, 1)"
                        fontSize="16px"
                        fontWeight="700"
                        lineHeight="23px"
                      >
                        {t("choose")}
                      </Text>
                    )}
                  </Flex>
                  <Text fontSize="16px" fontWeight="500" lineHeight="23px">
                    {t("to")}
                  </Text>
                  <Flex
                    mt="8px"
                    mb="16px"
                    px="16px"
                    py="8px"
                    border="1px solid rgba(0, 0, 0, 0.1)"
                    borderRadius="8px"
                  >
                    {selectedDays[1] ? (
                      <Text
                        color="rgba(51, 51, 51, 1)"
                        fontSize="16px"
                        fontWeight="700"
                        lineHeight="23px"
                      >
                        {selectedDays[1]?.format("MM/DD")}, {selectedEndClock}
                      </Text>
                    ) : (
                      <Text
                        color="rgba(170, 170, 170, 1)"
                        fontSize="16px"
                        fontWeight="700"
                        lineHeight="23px"
                      >
                        {t("choose")}
                      </Text>
                    )}
                  </Flex>
                </Box>
              </Box>
            </Collapse>
            <Flex mb="100px">
              <Button
                color="white"
                bg="rgba(51, 116, 246, 1)"
                mt="24px"
                flex={1}
                leftIcon={<SearchIcon size="24px" />}
                onClick={closeAndSearch}
              >
                {t("search")}
              </Button>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <Drawer
        autoFocus={false}
        size="full"
        isOpen={isTimeSelectorDrawerOpen}
        placement="right"
        onClose={onTimeSelectorDrawerClose}
      >
        <DrawerOverlay />
        <DrawerContent overflow="scroll">
          <DrawerCloseButton size="lg" />
          <DrawerHeader>{t("date")}</DrawerHeader>
          <DrawerBody>
            <TimeSelectorContent />
            <Flex mb="100px">
              <Button
                flex="1"
                color="white"
                bg="rgba(51, 116, 246, 1)"
                onClick={closeAndSearch}
                leftIcon={<SearchIcon size="24px" />}
              >
                {t("search")}
              </Button>
            </Flex>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
      <Box cursor="pointer" w="100%" onClick={onAddressTimeDrawerOpen}>
        {isSameAddress ? (
          <Flex align="center">
            <Text
              maxW={locale === "zh-TW" ? "230px" : "205px"}
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
              fontSize="20px"
              fontWeight="700"
              lineHeight="28px"
              mr="8px"
            >
              {selectedStation && selectedStation !== "chargeZone"
                ? t(`station_i18n.${selectedStation}`, {
                    defaultValue: selectedStation,
                  }) +
                  "，" +
                  t(`city_i18n.${selectedCity}`, {
                    defaultValue: selectedCity,
                  })
                : t(`zone_i18n.${selectedZone}`, {
                    defaultValue: selectedZone,
                  }) +
                  "，" +
                  t(`city_i18n.${selectedCity}`, {
                    defaultValue: selectedCity,
                  })}
            </Text>
            <Text
              fontWeight="400"
              borderRadius="2px"
              px="4px"
              fontSize="12px"
              lineHeight="16px"
              bg="black"
              color="white"
            >
              {t("pickupAndReturn")}
            </Text>
          </Flex>
        ) : (
          <Box>
            <Flex>
              <Text
                maxW={locale === "zh-TW" ? "230px" : "205px"}
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                fontSize="12px"
                fontWeight="700"
                lineHeight="16px"
                mr="8px"
              >
                {selectedStation && selectedStation !== "chargeZone"
                  ? t(`station_i18n.${selectedStation}`, {
                      defaultValue: selectedStation,
                    }) +
                    "，" +
                    t(`city_i18n.${selectedCity}`, {
                      defaultValue: selectedCity,
                    })
                  : t(`zone_i18n.${selectedZone}`, {
                      defaultValue: selectedZone,
                    }) +
                    "，" +
                    t(`city_i18n.${selectedCity}`, {
                      defaultValue: selectedCity,
                    })}
              </Text>
              <Text
                fontWeight="400"
                borderRadius="2px"
                px="4px"
                fontSize="12px"
                lineHeight="16px"
                bg="black"
                color="white"
              >
                {t("start")}
              </Text>
            </Flex>
            <Flex my="2px">
              <Text
                maxW={locale === "zh-TW" ? "230px" : "205px"}
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
                fontSize="12px"
                fontWeight="700"
                lineHeight="16px"
                mr="8px"
              >
                {selectedReturnStation && selectedReturnStation !== "chargeZone"
                  ? t(`station_i18n.${selectedReturnStation}`, {
                      defaultValue: selectedReturnStation,
                    }) +
                    "，" +
                    t(`city_i18n.${selectedReturnCity}`, {
                      defaultValue: selectedReturnCity,
                    })
                  : t(`zone_i18n.${selectedReturnZone}`, {
                      defaultValue: selectedReturnZone,
                    }) +
                    "，" +
                    t(`city_i18n.${selectedReturnCity}`, {
                      defaultValue: selectedReturnCity,
                    })}
              </Text>
              <Text
                fontWeight="400"
                borderRadius="2px"
                px="4px"
                fontSize="12px"
                lineHeight="16px"
                bg="black"
                color="white"
              >
                {t("end")}
              </Text>
            </Flex>
          </Box>
        )}

        <Flex align="center" mt="2px">
          {selectedDays.length === 2 &&
          selectedStartClock !== "" &&
          selectedEndClock !== "" ? (
            <>
              <Text
                fontSize="12px"
                fontWeight="400"
                lineHeight="16px"
                color="#748598"
              >
                {selectedDays[0]?.format("MM/DD")}, {selectedStartClock}
                {" - "}
              </Text>
              <Text
                fontSize="12px"
                fontWeight="400"
                lineHeight="16px"
                color="#748598"
              >
                {selectedDays[1]?.format("MM/DD")}, {selectedEndClock}
              </Text>
            </>
          ) : (
            <Text
              fontSize="12px"
              fontWeight="400"
              lineHeight="16px"
              color="#748598"
            >
              {t("whenPickup")}
            </Text>
          )}
        </Flex>
      </Box>

      <Flex
        p="12px"
        bg="#3374F6"
        align="center"
        justify="center"
        borderRadius="8px"
        cursor="pointer"
        onClick={onAddressTimeDrawerOpen}
      >
        <EditIcon w="24px" h="24px" />
      </Flex>
    </Flex>
  );
};

const HomeTimeSearchBar = ({ pageState }) => {
  const session = useSession();
  const { t } = useTranslation("common");
  const {
    selectedCity,
    selectedZone,
    selectedStation,
    selectedReturnCity,
    selectedReturnZone,
    selectedReturnStation,
    selectedDays,
    selectedStartClock,
    tempSelectedCity,
    tempSelectedZone,
    tempSelectedStation,
    tempSelectedZoneId,
    selectedEndClock,
    latestOrderTimeUnion,
    isAirport,
    isAirportReturn,
    isOpenCitySelect,
    isOpenReturnCitySelect,
    isSameAddress,
    tempSelectedReturnCity,
    tempSelectedReturnZone,
    tempSelectedReturnStation,
    tempSelectedReturnZoneId,
  } = appStore;
  const [isOpen, setIsOpen] = useState(false);

  const {
    isOpen: isFilterOpen,
    onOpen: onFilterOpen,
    onClose: onFilterClose,
  } = useDisclosure();

  const goNext = () => {
    if (!isSameAddress) {
      appStore.isOpenCitySelect = false;
      appStore.isOpenReturnCitySelect = true;
    } else {
      if (isAirport === 0) {
        if (tempSelectedCity === "") {
          toastErrorAndReport(
            `${t("choose")}${t("city")}`,
            "HomeTimeSearchBar/goNext/city"
          );
          return;
        }
        if (tempSelectedZone === "") {
          toastErrorAndReport(
            `${t("choose")}${t("zone")}/${t("address")}`,
            "HomeTimeSearchBar/goNext/zone"
          );
          return;
        }
      }
      appStore.isOpenCitySelect = false;
      setIsOpen(true);
    }
  };

  const closeAndSearch = () => {
    const onClose = () => {
      setIsOpen(false);
      appStore.isOpenCitySelect = false;
    };
    search(t, onClose);
  };

  useEffect(() => {
    if (isSameAddress) {
      appStore.tempSelectedReturnCity = tempSelectedCity;
      appStore.tempSelectedReturnZone = tempSelectedZone;
      appStore.tempSelectedReturnStation = tempSelectedStation;
      appStore.tempSelectedReturnZoneId = tempSelectedZoneId;
      appStore.isAirportReturn = isAirport;
    }
  }, [
    isAirport,
    isSameAddress,
    tempSelectedCity,
    tempSelectedStation,
    tempSelectedZone,
    tempSelectedZoneId,
  ]);

  useEffect(() => {
    if (
      tempSelectedCity !== tempSelectedReturnCity ||
      tempSelectedZone !== tempSelectedReturnZone ||
      tempSelectedStation !== tempSelectedReturnStation ||
      isAirport !== isAirportReturn
    ) {
      appStore.isSameAddress = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tempSelectedReturnCity,
    tempSelectedReturnStation,
    tempSelectedReturnZone,
    isAirportReturn,
  ]);

  // HOTFIX:
  // 因為高度在 720 以下時，Popover 會被蓋住，所以要把他往上移
  const { height } = useWindowDimensions();

  const isHeightLessThan720 = useMemo(() => {
    if (height) {
      return height < 720;
    }
    return null;
  }, [height]);

  if (isHeightLessThan720 === null) return <></>;

  return (
    <>
      <MobileBar
        display={{ md: isHeightLessThan720 ? "flex" : "none", base: "flex" }}
        isFilterOpen={isFilterOpen}
        onFilterClose={onFilterClose}
        onFilterOpen={onFilterOpen}
        latestOrderTimeUnion={latestOrderTimeUnion}
      />

      {/* Desktop */}
      <Box
        display={{ base: "none", md: isHeightLessThan720 ? "none" : "flex" }}
        my="20px"
        alignItems="center"
        justifyContent="center"
      >
        <Flex
          p="4px"
          border="1px solid rgba(0, 0, 0, 0.1)"
          borderRadius="8px"
          align="center"
          pos="relative"
        >
          <Popover
            isOpen={isOpenCitySelect}
            zIndex="99"
            placement="bottom-start"
            onClose={() => {
              appStore.isOpenCitySelect = false;
            }}
          >
            <PopoverTrigger>
              <Flex
                py="6px"
                px="8px"
                w="180px"
                cursor="pointer"
                alignItems="center"
                onClick={() => {
                  appStore.isOpenCitySelect = !isOpenCitySelect;
                }}
              >
                {selectedCity != "" && selectedZone != "" ? (
                  <>
                    <Text
                      fontSize="14px"
                      fontWeight="400"
                      lineHeight="22px"
                      mr="4px"
                      color="#748598"
                    >
                      {t("pickup")}
                    </Text>
                    <Text
                      w="150px"
                      textAlign="start"
                      fontSize="14px"
                      fontWeight="700"
                      lineHeight="20px"
                      color="#333333"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {t(`city_i18n.${selectedCity}`, {
                        defaultValue: selectedCity,
                      })}
                      {selectedZone !== "" &&
                        selectedZone !== null &&
                        `,${t(`zone_i18n.${selectedZone}`, {
                          defaultValue: selectedZone,
                        })}`}
                      {selectedStation !== "" &&
                        selectedStation !== null &&
                        selectedStation !== "chargeZone" &&
                        `,${t(`station_i18n.${selectedStation}`, {
                          defaultValue: selectedStation,
                        })}`}
                    </Text>
                  </>
                ) : (
                  <Text
                    color="rgba(136, 136, 136, 1)"
                    fontSize="14px"
                    fontWeight="500"
                    lineHeight="20px"
                  >
                    {t("wherePickup")}
                  </Text>
                )}
              </Flex>
            </PopoverTrigger>
            <PopoverContent backgroundColor="white" flex="1" width="400px">
              <PopoverArrow />
              <PopoverBody p="24px">
                <AddressInputContent type="pickup" />
                <Divider />
                <Flex p="24px">
                  <Button
                    flex="1"
                    mx="5px"
                    variant="ghost"
                    onClick={() => {
                      appStore.tempSelectedCity = "";
                      appStore.tempSelectedZone = "";
                      appStore.tempSelectedStation = "";
                    }}
                  >
                    {t("clearAll")}
                  </Button>
                  <Button
                    flex="1"
                    mx="5px"
                    color="white"
                    bg="rgba(51, 116, 246, 1)"
                    onClick={goNext}
                  >
                    {isSameAddress
                      ? t("useCarTime")
                      : `${t("choose")}${t("dropoff_location")}`}
                  </Button>
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Popover>

          <Popover
            isOpen={isOpenReturnCitySelect}
            zIndex="99"
            placement="bottom-start"
            onClose={() => {
              appStore.isOpenReturnCitySelect = false;
            }}
          >
            <PopoverTrigger>
              <Flex
                alignItems="center"
                borderRight="1px"
                borderColor="rgba(196, 196, 196, 1)"
                px="8px"
                w="180px"
                cursor="pointer"
                onClick={() => {
                  appStore.isOpenReturnCitySelect = !isOpenReturnCitySelect;
                }}
              >
                {isSameAddress ? (
                  <Text
                    fontSize="14px"
                    fontWeight="500"
                    lineHeight="20px"
                    color="#748598"
                  >
                    {t("dropoff_sameLocation")}
                  </Text>
                ) : selectedReturnCity != "" && selectedReturnZone != "" ? (
                  <>
                    <Text
                      fontSize="14px"
                      fontWeight="400"
                      lineHeight="22px"
                      mr="4px"
                      color="#748598"
                    >
                      {t("dropoff")}
                    </Text>
                    <Text
                      w="150px"
                      textAlign="start"
                      fontSize="14px"
                      fontWeight="700"
                      lineHeight="20px"
                      color="#333333"
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {t(`city_i18n.${selectedReturnCity}`, {
                        defaultValue: selectedReturnCity,
                      })}
                      {selectedReturnZone !== "" &&
                        selectedReturnZone !== null &&
                        `,${t(`zone_i18n.${selectedReturnZone}`, {
                          defaultValue: selectedReturnZone,
                        })}`}
                      {selectedReturnStation !== "" &&
                        selectedReturnStation !== null &&
                        selectedReturnStation !== "chargeZone" &&
                        `,${t(`station_i18n.${selectedReturnStation}`, {
                          defaultValue: selectedReturnStation,
                        })}`}
                    </Text>
                  </>
                ) : (
                  <Text
                    color="rgba(136, 136, 136, 1)"
                    fontSize="14px"
                    fontWeight="500"
                    lineHeight="20px"
                  >
                    {t("wherePickup")}
                  </Text>
                )}
              </Flex>
            </PopoverTrigger>
            <PopoverContent backgroundColor="white" flex="1" width="400px">
              <PopoverArrow />
              <PopoverBody p="24px">
                <AddressInputContent type="return" />
                <Divider />
                <Flex p="24px">
                  <Button
                    flex="1"
                    mx="5px"
                    variant="ghost"
                    onClick={() => {
                      appStore.tempSelectedReturnCity = "";
                      appStore.tempSelectedReturnZone = "";
                      appStore.tempSelectedReturnStation = "";
                    }}
                  >
                    {t("clearAll")}
                  </Button>
                  <Button
                    flex="1"
                    mx="5px"
                    color="white"
                    bg="rgba(51, 116, 246, 1)"
                    onClick={() => {
                      if (isAirportReturn === 0) {
                        if (tempSelectedReturnCity === "") {
                          toastErrorAndReport(
                            `${t("choose")}${t("city")}`,
                            "HomeTimeSeachBar/useCarTime/returnCity"
                          );
                          return;
                        }
                        if (tempSelectedReturnZone === "") {
                          toastErrorAndReport(
                            `${t("choose")}${t("zone")}/${t("address")}`,
                            "HomeTimeSeachBar/useCarTime/returnZone"
                          );
                          return;
                        }
                      }
                      appStore.isOpenCitySelect = false;
                      appStore.isOpenReturnCitySelect = false;
                      setIsOpen(true);
                    }}
                  >
                    {t("useCarTime")}
                  </Button>
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Popover>
          <Popover
            isOpen={isOpen}
            zIndex="99"
            placement="bottom"
            onClose={() => {
              setIsOpen(false);
            }}
          >
            <PopoverTrigger>
              <Box
                ml="8px"
                maxW="300px"
                flex={1}
                display="flex"
                cursor="pointer"
                onClick={() => {
                  setIsOpen((state) => !state);
                }}
              >
                <Flex alignItems="center" flex={1} w="150px">
                  {selectedDays?.length === 2 && selectedStartClock != "" ? (
                    <Box>
                      <Text fontSize="14px" fontWeight="700" lineHeight="20px">
                        {selectedDays[0].format("MM/DD")}, {selectedStartClock}
                      </Text>
                    </Box>
                  ) : (
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      lineHeight="20px"
                      color="rgba(136, 136, 136, 1)"
                    >
                      {t("pickupTime")}
                    </Text>
                  )}
                </Flex>
                <SwitchIcon size="24px" mr="8px" />
                <Flex alignItems="center" flex={1} w="150px">
                  {selectedDays?.length === 2 && selectedEndClock != "" ? (
                    <Box>
                      <Text fontSize="14px" fontWeight="700" lineHeight="20px">
                        {selectedDays[1].format("MM/DD")}, {selectedEndClock}
                      </Text>
                    </Box>
                  ) : (
                    <Text
                      fontSize="14px"
                      fontWeight="500"
                      lineHeight="20px"
                      color="rgba(136, 136, 136, 1)"
                    >
                      {t("returnTime")}
                    </Text>
                  )}
                </Flex>
              </Box>
            </PopoverTrigger>
            <PopoverContent
              backgroundColor="white"
              flex="1"
              width={{ md: "600px", base: "400px" }}
              overflow="scroll"
            >
              <PopoverArrow />
              <PopoverBody>
                <TimeSelectorContent />
                <Flex px="24px">
                  <Button
                    flex="1"
                    mr="16px"
                    mb="16px"
                    variant="ghost"
                    onClick={() => {
                      appStore.tempSelectedDays = [];
                      appStore.tempSelectedStartClock = "";
                      appStore.tempSelectedEndClock = "";
                    }}
                  >
                    {t("clean")}
                  </Button>
                  <Button
                    flex="1"
                    color="white"
                    bg="rgba(51, 116, 246, 1)"
                    onClick={closeAndSearch}
                    leftIcon={<SearchIcon size="24px" />}
                  >
                    {t("search")}
                  </Button>
                </Flex>
              </PopoverBody>
            </PopoverContent>
          </Popover>
          <Button
            size="sm"
            fontSize="14px"
            color="white"
            bg="rgba(51, 116, 246, 1)"
            onClick={closeAndSearch}
            leftIcon={<SearchIcon size="24px" />}
          >
            {t("search")}
          </Button>
        </Flex>
      </Box>
    </>
  );
};
export default HomeTimeSearchBar;
